import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import PropTypes from "prop-types";
import Loader from "components/Loader";
import LanguageSelect from "components/LanguageSelect";
import { tempOptions, NotifyContent } from "components/Notify";
import { store as reduxStore } from "index";
import { Store as notifyStore } from "react-notifications-component";
import * as coreApi from "services/core-iam";
import keys from "configs/constants";
import "./style.css";
import utils from "utils";
import { getUserAttribute } from "layouts/common";
import { acquireToken, clearSessionStorage } from "services/backendService";
import { useTheme } from "@/theme/ThemeContext";
import { THEME_DARK, THEME_LIGHT } from "@/theme/theme.utils";

// The temperature and pressure index mappings live in utils because this form and
// SiteProperties must agree on them: the index is what gets sent to the API, so a
// second copy here could drift on one of the two paths only.

const normalizeUnit = (value, fallback) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const Preferences = ({ isOpen, toggle = () => {}, t = (key) => key }) => {
  const { setSavedTheme, clearThemeOverride } = useTheme();
  const [languageList, setLanguageList] = useState([]);
  const [values, setValues] = useState({
    languageValue: "",
    temperatureUnit: 0,
    pressureUnit: 0,
    theme: THEME_LIGHT,
  });
  const [initialValues, setInitialValues] = useState({
    languageValue: "",
    temperatureUnit: 0,
    pressureUnit: 0,
    theme: THEME_LIGHT,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadPreferences = useCallback(async () => {
    setIsLoading(true);
    try {
      const langList = [...reduxStore.getState().translations.list];
      setLanguageList(langList);

      const userAttributes = await getUserAttribute();

      const fallbackLangValue = langList[0]
        ? `${langList[0].text}_${langList[0].lang}`
        : "";

      const userLanguage = userAttributes?.language || "";
      const [userLangText = "", userLangCodeRaw = ""] = userLanguage.split("_");
      const userLangCode = userLangCodeRaw?.toLowerCase();
      const userLangCodeShort = userLangCode?.split("-")[0];

      const matchingLanguage =
        langList.find((l) => `${l.text}_${l.lang}` === userLanguage) ||
        langList.find((l) => l.lang?.toLowerCase() === userLangCode) ||
        langList.find(
          (l) => l.lang?.split("-")[0]?.toLowerCase() === userLangCodeShort
        ) ||
        langList.find(
          (l) => l.text?.toLowerCase() === userLangText?.toLowerCase()
        );

      const languageValue = matchingLanguage
        ? `${matchingLanguage.text}_${matchingLanguage.lang}`
        : fallbackLangValue;

      const temperatureUnit = utils.temperatureUnitToIndex(userAttributes?.temp_unit);
      const pressureUnit = utils.pressureUnitToIndex(userAttributes?.press_unit);
      const theme = userAttributes?.theme || THEME_LIGHT;

      const nextValues = {
        languageValue,
        temperatureUnit,
        pressureUnit,
        theme,
      };

      setValues(nextValues);
      setInitialValues({ ...nextValues });
    } catch (error) {
      if (!keys.IS_PROD) {
        utils.debug(error, utils.MSG_TYPE_WARN);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadPreferences();
    } else {
      setIsSaving(false);
    }
  }, [isOpen, loadPreferences]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    toggle(false);
    setValues({ ...initialValues });
  }, [initialValues, isSaving, toggle]);

  const update = (key, value) =>
    setValues((prev) => {
      const newValue =
        key === "languageValue" || key === "theme"
          ? value
          : Number.parseInt(value, 10);
      return { ...prev, [key]: newValue };
    });

  const isDirty = useMemo(
    () =>
      values.languageValue !== initialValues.languageValue ||
      Number(values.temperatureUnit) !==
        Number(initialValues.temperatureUnit) ||
      Number(values.pressureUnit) !== Number(initialValues.pressureUnit) ||
      values.theme !== initialValues.theme,
    [values, initialValues]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isDirty) {
      return;
    }

    setIsSaving(true);
    let success = true;
    const hasThemeChanged = values.theme !== initialValues.theme;

    const payload = {
      lang: values.languageValue,
      temp_unit: utils.indexToTemperatureUnit(values.temperatureUnit),
      press_unit: utils.indexToPressureUnit(values.pressureUnit),
      theme: values.theme,
    };

    try {
      await coreApi.patchSelfAsync(payload);

        if (hasThemeChanged) {
            setSavedTheme(values.theme);
            clearThemeOverride();
        }
        
      localStorage.removeItem("tempLanguage");
      clearSessionStorage();
      await acquireToken();
      globalThis.location.reload();
    } catch (error) {
      success = false;
      if (!keys.IS_PROD) {
        utils.debug(error, utils.MSG_TYPE_WARN);
      }
    } finally {
      setIsSaving(false);
      toggle(false);
      notifyStore.addNotification({
        ...tempOptions,
        content: NotifyContent(
          success ? "success" : "danger",
          null,
          t(success ? "sidebar.changesApplied" : "sidebar.changesNotApplied")
        ),
      });
    }
  };

  const temperatureUnits = useMemo(
    () => [
      { value: 0, label: "Celsius" },
      { value: 1, label: "Fahrenheit" },
    ],
    []
  );

  const pressureUnits = useMemo(
    () => [
      { value: 0, label: "bar" },
      { value: 1, label: "PSI" },
      { value: 2, label: "kPa" },
    ],
    []
  );

  const themeOptions = useMemo(
    () => [
      { value: THEME_LIGHT, label: t("websitePreferences.theme.light") },
      { value: THEME_DARK, label: t("websitePreferences.theme.dark") },
    ],
    [t]
  );

  const closeLabel = t("generic.close");
  const closeButton = (
    <button
      type="button"
      className="close"
      onClick={handleClose}
      aria-label={closeLabel}
    >
      <span aria-hidden="true">{"\u00d7"}</span>
    </button>
  );

  const normalizedTemperatureUnit = normalizeUnit(values.temperatureUnit, 0);
  const normalizedPressureUnit = normalizeUnit(values.pressureUnit, 0);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      backdrop
      fade
      contentClassName="border-0 shadow"
    >
      <ModalHeader
        toggle={handleClose}
        className="border-0 pb-0 align-items-center"
        close={closeButton}
      >
        {t("websitePreferences.title")}
      </ModalHeader>

      <ModalBody className="pt-3 position-relative">
        {isLoading && (
          <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center">
            <Loader />
          </div>
        )}
        <Form onSubmit={handleSubmit} className={isLoading ? "opacity-50" : ""}>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label className="form-control-label" htmlFor="languageSelect">
                  {t("websitePreferences.language.label")}
                </Label>

                <LanguageSelect
                  id="languageSelect"
                  languages={languageList}
                  value={values.languageValue}
                  onChange={(val) => update("languageValue", val)}
                  disabled={isLoading || isSaving}
                />

                <small className="form-text text-muted">
                  {t("websitePreferences.language.help")}
                </small>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label className="form-control-label" htmlFor="tempUnitSelect">
                  {t("websitePreferences.temperature.label")}
                </Label>
                <Input
                  id="tempUnitSelect"
                  type="select"
                  className="custom-select"
                  value={normalizedTemperatureUnit}
                  onChange={(e) => update("temperatureUnit", e.target.value)}
                  disabled={isLoading || isSaving}
                >
                  {temperatureUnits.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
                <small className="form-text text-muted">
                  {t("websitePreferences.temperature.help")}
                </small>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label
                  className="form-control-label"
                  htmlFor="pressureUnitSelect"
                >
                  {t("websitePreferences.pressure.label")}
                </Label>
                <Input
                  id="pressureUnitSelect"
                  type="select"
                  className="custom-select"
                  value={normalizedPressureUnit}
                  onChange={(e) => update("pressureUnit", e.target.value)}
                  disabled={isLoading || isSaving}
                >
                  {pressureUnits.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
                <small className="form-text text-muted">
                  {t("websitePreferences.pressure.help")}
                </small>
              </FormGroup>
            </Col>

            <Col md="12">
                  <FormGroup>
                    <Label className="form-control-label" htmlFor="themeSelect">
                      {t("websitePreferences.theme.label")}
                    </Label>
                    <Input
                        id="themeSelect"
                        type="select"
                        className="custom-select"
                        value={values.theme}
                        onChange={(e) => update("theme", e.target.value)}
                        disabled={isLoading || isSaving}
                    >
                      {themeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                      ))}
                    </Input>
                    <small className="form-text text-muted">
                      {t("websitePreferences.theme.help")}
                    </small>
                  </FormGroup>
                </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            <Button
              type="button"
              color="link"
              className="text-muted mr-2"
              onClick={handleClose}
              disabled={isSaving}
            >
              {t("generic.cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!isDirty || isSaving || isLoading}
            >
              {t("generic.apply")}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

Preferences.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func,
  t: PropTypes.func,
};

export default Preferences;
